import { Box, MenuItem, Select, Tabs, Tab } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useResourceAppVersionProvider } from "../../applications/components/ResourceAppVersionProvider";
import {
  ErrorMessage,
  resolveAPIErrorMessage,
} from "modus/components/error-message/ErrorMessage";
import { Skeleton } from "@material-ui/lab";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { FormattedMessage } from "react-intl";
import VersionCreator from "./VerisonCreator";
import { DeploymentList } from "./DeploymentList";
import { VersionRemover } from "./VersionRemover";
import Loader from "modus/components/misc/Loader";
import {ResourceAuthorizer} from "../../authorizer/components/Authorizer";
import ApiSpecificationTab from "../pages/application-summary-page/ApiSpecificationTab";
import { useSelector } from "react-redux";
import { useCurrentTeam } from "libs/security/authorization/Permission";

interface ResourceVersionProps {
  applicationName: string;
  applicationId: string;
  basePath: string;
  serviceId: string;
  isFlattenedApplication?: boolean;
}
export const ResourceVersion: React.FC<ResourceVersionProps> = ({
  applicationName,
  applicationId,
  basePath,
  serviceId,
  isFlattenedApplication = false,
}) => {
  const [version, setVersion] = useState<string>("");
  const [tabValue, setTabValue] = useState<number>(0);

  // Linting enablement logic (same as product page)
  const lintingEnabledTeams = process.env.REACT_APP_LINTING_TEAM_IDS?.split(',') || [];
  const currentTeam = useSelector((state: any) => state?.teamManager?.teams?.currentTeam);
  const { teamId: storedTeamId } = useCurrentTeam();
  const teamId = currentTeam?.id || storedTeamId;
  const isLintingEnabled = process.env.REACT_APP_LINTING_RESULTS === "true" || lintingEnabledTeams.includes(teamId);

  const handleVersionChange = (version: string) => {
    setVersion(version);
  };
  
  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue);
  };

  const hasEmptyVersions = () => {
    return (
      versionState?.value &&
      versionState.loaded &&
      versionState?.value.length === 0
    );
  };

  const { versionState, getVersions } = useResourceAppVersionProvider(
    applicationId as string
  );
  useEffect(() => {
    if (!applicationId || !getVersions) {
      return;
    }
    getVersions();
  }, [applicationId, getVersions]);

  useEffect(() => {
    if (!setVersion || !versionState.value) {
      return;
    }
    if (versionState?.value.length > 0) {
      setVersion(versionState?.value[0]);
    }
  }, [versionState.value, setVersion]);
  return (
    <>
      {versionState?.value && !hasEmptyVersions() && (
        <ResourceAuthorizer action={"read"} resourceType={"SERVICE"} resourceId={serviceId} hideErrorMessage >
        <Box
          display="flex"
          flexDirection="row"
          justifyContent={"space-between"}
          alignItems={"center"}
          marginTop={"20px"}
        >
          <Box display="flex" alignItems="center" marginRight={"10px"}>
            <Select
              id="versions"
              IconComponent={ExpandMoreIcon}
              variant="outlined"
              style={{ minWidth: "20%" }}
              className="header-selector resource-version-select"
              value={version}
              onChange={(e) => handleVersionChange(e.target.value as string)}
            >
              {versionState?.loaded &&
                version?.length > 0 &&
                versionState?.value?.map((version: string) => {
                  return (
                    <MenuItem
                      value={version}
                      key={version}
                      onClick={() => handleVersionChange(version)}
                    >
                      {version}
                    </MenuItem>
                  );
                })}
            </Select>
            {versionState?.loaded && (
                <ResourceAuthorizer action={"delete"} resourceType={"SERVICE"} resourceId={serviceId} hideErrorMessage >
              <Box marginLeft={"10px"}>
                <VersionRemover
                  applicationName={applicationName}
                  applicationId={applicationId}
                  version={version}
                  refreshVersions={getVersions}
                />
              </Box>
                </ResourceAuthorizer>
            )}
          </Box>
          <ResourceAuthorizer action={"update"} resourceType={"SERVICE"} resourceId={serviceId} hideErrorMessage >
          <VersionCreator
            getVersions={getVersions}
            apiId={applicationId}
            isFlattenedApplication={isFlattenedApplication}
          />
          </ResourceAuthorizer>
        </Box>
        </ResourceAuthorizer>
      )}
      <Box mt={2}>
        {versionState?.loading && (
          <>
            <Loader showBackDrop />
            <Skeleton animation="wave" width="100%" height="900px" />
          </>
        )}
        {versionState?.value && !hasEmptyVersions() && (
          <>
            {/* Tabs for Deployment and API Specification */}
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
              style={{ marginBottom: "20px" }}
            >
              <Tab label="DEPLOYMENT" />
              <Tab label="DOCUMENTATION" />
            </Tabs>

            {/* Tab content */}
            {tabValue === 0 && (
              <DeploymentList
                applicationName={applicationName}
                consumerKey={applicationId}
                version={version}
                basePath={basePath}
                serviceId={serviceId}
                isFlattenedApplication={isFlattenedApplication}
              />
            )}

            {tabValue === 1 && version && (
              <ApiSpecificationTab 
                serviceId={serviceId}
                consumerKey={applicationId}
                applicationName={applicationName}
                isLintingEnabled={isLintingEnabled}
                version={version}
              />
            )}
          </>
        )}
        {versionState?.loaded && versionState?.error && (
          <ErrorMessage
            message={
              <ResourceAuthorizer action={"update"} resourceType={"SERVICE"} resourceId={"serviceId"} >
              <Box display="flex" flexDirection="column">
                Create a new Version
                <Box mt={1} />
                <VersionCreator
                  getVersions={getVersions}
                  apiId={applicationId}
                  isFlattenedApplication={isFlattenedApplication}
                />
              </Box>
              </ResourceAuthorizer>
            }
          />
        )}
        {versionState?.error && (
          <ErrorMessage
            message={
              (versionState?.errorValue as any)?.response?.status === 403
                ? "You are not having access to view or edit Resource versions"
                : resolveAPIErrorMessage(versionState?.errorValue)
            }
          />
        )}
      </Box>
      {hasEmptyVersions() && (
          <ResourceAuthorizer action={"update"} resourceType={"SERVICE"} resourceId={serviceId} >
          <Box
          mt={2}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height={250}
          style={{ border: "1px solid" }}
        >
          <Box>
            <FormattedMessage defaultMessage="No version details available. Create a new Version" />
          </Box>
          <Box style={{ marginTop: "20px" }}></Box>
          <VersionCreator
            getVersions={getVersions}
            apiId={applicationId}
            isFlattenedApplication={isFlattenedApplication}
          />
          <Box style={{ marginTop: "20px" }}></Box>
        </Box>
          </ResourceAuthorizer>
      )}
    </>
  );
};
